import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PlaceholderPosition } from '@/types/quote';
import { PlaceholderPropertyEditor } from './PlaceholderPropertyEditor';
import { NudgeControls } from './NudgeControls';
import { PlaceholderHelpPopup } from './PlaceholderHelpPopup';
import { createDefaultPlaceholder } from '@/lib/pdfTemplateRenderer';
import { Trash2, Plus, ChevronUp, ChevronDown, Search, HelpCircle, Info, Keyboard } from 'lucide-react';
import { toast } from 'sonner';

interface PlaceholderItem {
  key: string;
  description: string;
  category: string;
}

interface DesignerRightSidebarProps {
  selectedIndices: Set<number>;
  placeholders: PlaceholderPosition[];
  onUpdate: (index: number, updates: Partial<PlaceholderPosition>) => void;
  onDelete: (index: number) => void;
  onNudge: (direction: 'up' | 'down' | 'left' | 'right', amount: number) => void;
  onCenter: () => void;
  nudgeStepSize: number;
  setNudgeStepSize: (size: number) => void;
  availablePlaceholders: PlaceholderItem[];
  currentPage: number;
  snapPosition: (x: number, y: number) => { x: number; y: number };
  setPlaceholders: React.Dispatch<React.SetStateAction<PlaceholderPosition[]>>;
  setSelectedIndices: React.Dispatch<React.SetStateAction<Set<number>>>;
  setLastSelectedIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

export function DesignerRightSidebar({
  selectedIndices,
  placeholders,
  onUpdate,
  onDelete,
  onNudge,
  onCenter,
  nudgeStepSize,
  setNudgeStepSize,
  availablePlaceholders,
  currentPage,
  snapPosition,
  setPlaceholders,
  setSelectedIndices,
  setLastSelectedIndex,
}: DesignerRightSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const filteredPlaceholders = availablePlaceholders.filter(p =>
    p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleQuickAdd = (key: string) => {
    const tempPlaceholder = createDefaultPlaceholder(key, 0, 0);
    const centerX = (595 - tempPlaceholder.width) / 2;
    const centerY = (842 - tempPlaceholder.height) / 2;
    const snapped = snapPosition(centerX, centerY);

    const newPlaceholder = createDefaultPlaceholder(key, snapped.x, snapped.y);
    newPlaceholder.page = currentPage;

    setPlaceholders(prev => [...prev, newPlaceholder]);
    const newIndex = placeholders.length;
    setSelectedIndices(new Set([newIndex]));
    setLastSelectedIndex(newIndex);

    toast.success(`Veld "${key}" toegevoegd`);
    setSearchQuery('');
  };

  const deleteAllSelected = () => {
    setPlaceholders(prev => prev.filter((_, i) => !selectedIndices.has(i)));
    setSelectedIndices(new Set());
    toast.success(`${selectedIndices.size} veld(en) verwijderd`);
  };

  return (
    <Card className="w-60 min-w-[200px] border-l flex-shrink-0 flex flex-col h-full overflow-hidden">
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {selectedIndices.size === 1 ? (
            <>
              {/* Single selection */}
              <div className="space-y-4">
                <NudgeControls
                  onNudge={onNudge}
                  onCenter={onCenter}
                  stepSize={nudgeStepSize}
                  setStepSize={setNudgeStepSize}
                />
                <PlaceholderPropertyEditor
                  placeholder={placeholders[Array.from(selectedIndices)[0]]}
                  onChange={(updates) => onUpdate(Array.from(selectedIndices)[0], updates)}
                  onDelete={() => onDelete(Array.from(selectedIndices)[0])}
                />
              </div>
            </>
          ) : selectedIndices.size > 1 ? (
            <>
              {/* Multiple selection */}
              <div className="space-y-4">
                <NudgeControls
                  onNudge={onNudge}
                  onCenter={onCenter}
                  stepSize={nudgeStepSize}
                  setStepSize={setNudgeStepSize}
                />
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-xs font-semibold mb-2">{selectedIndices.size} velden geselecteerd</p>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="w-full h-7 text-xs"
                    onClick={deleteAllSelected}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Alles verwijderen
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* No selection */}
              <div className="text-center py-3 px-2 bg-muted/50 rounded-lg">
                <Info className="h-5 w-5 mx-auto mb-1.5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Selecteer een veld om te bewerken
                </p>
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Of sleep een veld vanuit links
                </p>
              </div>

              {/* Keyboard shortcuts */}
              <Collapsible open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between h-6 px-1.5">
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Keyboard className="h-3 w-3" />
                      Sneltoetsen
                    </span>
                    {shortcutsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-1">
                  <KeyboardShortcut keys={['Ctrl', 'S']} description="Opslaan" />
                  <KeyboardShortcut keys={['Ctrl', 'Z']} description="Ongedaan maken" />
                  <KeyboardShortcut keys={['Ctrl', 'Shift', 'Z']} description="Opnieuw" />
                  <KeyboardShortcut keys={['Ctrl', 'C']} description="Kopiëren" />
                  <KeyboardShortcut keys={['Ctrl', 'V']} description="Plakken" />
                  <KeyboardShortcut keys={['Ctrl', 'D']} description="Dupliceren" />
                  <KeyboardShortcut keys={['Ctrl', 'A']} description="Alles selecteren" />
                  <KeyboardShortcut keys={['Alt', 'L/R/T/B']} description="Uitlijnen" />
                  <KeyboardShortcut keys={['Alt', 'C']} description="Centreren" />
                  <KeyboardShortcut keys={['G']} description="Grid aan/uit" />
                  <KeyboardShortcut keys={['←', '↑', '→', '↓']} description="Verplaatsen" />
                  <KeyboardShortcut keys={['Del']} description="Verwijderen" />
                  <KeyboardShortcut keys={['Esc']} description="Deselecteren" />
                </CollapsibleContent>
              </Collapsible>
            </>
          )}

          {/* Quick Add Section */}
          <div className="pt-1.5 border-t">
            <Collapsible open={quickAddOpen} onOpenChange={setQuickAddOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between h-6 px-1.5">
                  <span className="flex items-center gap-1 text-[10px] font-medium">
                    <Plus className="h-3 w-3" />
                    Snel toevoegen
                  </span>
                  {quickAddOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-1.5 space-y-1.5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Zoek veld..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-7 h-6 text-xs"
                  />
                </div>

                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {filteredPlaceholders.slice(0, 12).map((p) => (
                    <PlaceholderHelpPopup key={p.key} placeholderKey={p.key}>
                      <button
                        type="button"
                        onClick={() => handleQuickAdd(p.key)}
                        className="w-full text-left px-1.5 py-1 text-xs rounded hover:bg-accent transition-colors flex items-center gap-1.5 group"
                      >
                        <Plus className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[9px] truncate">{p.key}</div>
                          <div className="text-[8px] text-muted-foreground truncate">{p.description}</div>
                        </div>
                        <HelpCircle className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </button>
                    </PlaceholderHelpPopup>
                  ))}
                  {filteredPlaceholders.length === 0 && searchQuery && (
                    <p className="text-[10px] text-muted-foreground text-center py-1.5">Geen velden gevonden</p>
                  )}
                  {filteredPlaceholders.length > 12 && (
                    <p className="text-[9px] text-muted-foreground text-center py-0.5">
                      +{filteredPlaceholders.length - 12} meer...
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </ScrollArea>
    </Card>
  );
}

// Keyboard shortcut helper
function KeyboardShortcut({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-center gap-1 text-[9px]">
      {keys.map((key, i) => (
        <span key={i} className="flex items-center gap-0.5">
          {i > 0 && <span className="text-muted-foreground">+</span>}
          <kbd className="px-1 py-0.5 bg-muted rounded text-[8px] font-mono border border-border">
            {key}
          </kbd>
        </span>
      ))}
      <span className="text-muted-foreground ml-0.5">{description}</span>
    </div>
  );
}
